<script setup lang="ts">
const { t } = useI18n()
const container = useTemplateRef('container')
const animationRotating = useTemplateRef('animationRotating')
const isVisible = useElementVisibility(container)

const currentSlotIndex = ref(0)
const totalColumnsToDisplay = ref(1)
const typewriterText = ref('')

const completion: InputCompletion = {
  color: 'purple',
  key: 'ask',
  label: 'ask',
}

const columns: SchemaTableColumn[] = [
  {
    icon: getIcon('name'),
    key: 'name',
    size: 2,
    title: t('columns.name'),
  },
  {
    icon: getIcon('ask'),
    key: 'question1',
    title: t('columns.bootstrapped'),
  },
  {
    icon: getIcon('ask'),
    key: 'question2',
    title: t('columns.sellData'),
  },

]

const rows: SchemaTableRow[] = [
  {
    image: getLogo('thecompaniesapi.com', 'companies')!,
    name: 'The Companies API',
    question1: 'yes',
    question2: 'yes',
  },
  {
    image: getLogo('stark-industries.com', 'companies')!,
    name: 'Stark Industries',
    question1: 'no',
    question2: 'no',
  },
  {
    image: getLogo('vault-tec.corp', 'companies')!,
    name: 'Vault-Tec Corporation',
    question1: 'no',
    question2: 'no',
  },
  {
    image: getLogo('wayne-enterprises.com', 'companies')!,
    name: 'Wayne Enterprises',
    question1: 'no',
    question2: 'no',
  },
  {
    image: getLogo('waystar-royco.com', 'companies')!,
    name: 'Waystar Royco',
    question1: 'no',
    question2: 'yes',
  },
]

const slots = [
  'question1',
  'question1-loading',
  'question2',
  'question2-loading',
  'completed',
]

const columnsToDisplay = computed(() => {
  return columns.slice(0, totalColumnsToDisplay.value)
})

const isLoading = computed(() => {
  return slots[currentSlotIndex.value].endsWith('-loading')
})

watch(currentSlotIndex, (value) => {
  typewriterText.value = ''

  if (value % 2 === 1) {
    totalColumnsToDisplay.value = Math.min(totalColumnsToDisplay.value + 1, 3)
  }
})

watch(isVisible, (value) => {
  if (value) {
    currentSlotIndex.value = 0
    totalColumnsToDisplay.value = 1
    animationRotating.value?.reset()
  }
})
</script>

<template>
  <div
    ref="container"
    class="flex size-full min-h-96 flex-col gap-12 p-4 lg:items-center lg:justify-center"
  >
    <Transition
      enter-active-class="transition-base delay-50 transform-gpu"
      enter-from-class="opacity-0 translate-y-24"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-base"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-24"
    >
      <SchemaTable
        v-if="isVisible"
        :columns="columnsToDisplay"
        :loadable-columns="['question1', 'question2']"
        :loading-text="t('globals.thinking')"
        :rows="rows"
      />
    </Transition>

    <div class="absolute bottom-8 left-0 w-full px-6">
      <Transition
        enter-active-class="transition-base delay-150"
        enter-from-class="opacity-0 translate-y-24"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-base"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-24"
      >
        <Prompt
          v-if="isVisible"
          border
          class="z-20"
          :completable="false"
          :completion="completion"
          :completions="[completion]"
          :hoverable="false"
          is-preview
          light
          :loading="isLoading"
          :show-cleanup="false"
          :submittable="!!typewriterText.length"
        >
          <template #input>
            <div class="overflow-hidden">
              <AnimationRotating
                ref="animationRotating"
                v-model="currentSlotIndex"
                direction="vertical"
                once
                :slots="slots"
              >
                <template
                  v-for="slot in slots"
                  :key="slot"
                  #[slot]
                >
                  <div class="flex items-center gap-1">
                    <template v-if="slot.startsWith('question') && slot.endsWith('-loading')">
                      <span class="flex items-center gap-2">
                        {{ t('globals.thinking') }}
                      </span>
                    </template>

                    <template v-else>
                      <AnimateTypewriter
                        v-model="typewriterText"
                        :speed="100"
                        :text="t(`prompts.${slot}`)"
                      />
                    </template>
                  </div>
                </template>
              </AnimationRotating>
            </div>
          </template>
        </Prompt>
      </Transition>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "columns": {
      "bootstrapped": "Bootstrapped?",
      "name": "Name",
      "sellData": "Sell data?"
    },
    "prompts": {
      "completed": "Ask me anything about your customers?",
      "question1": "Are they bootstrapped?",
      "question2": "Do they sell data?"
    }
  },
  "es": {
    "columns": {
      "bootstrapped": "¿Autofinanciado?",
      "name": "Nombre",
      "sellData": "¿Vende datos?"
    },
    "prompts": {
      "completed": "¿Pregúntame cualquier cosa sobre tus clientes?",
      "question1": "¿Están autofinanciados?",
      "question2": "¿Venden datos?"
    }
  },
  "fr": {
    "columns": {
      "bootstrapped": "Autofinancé?",
      "name": "Nom",
      "sellData": "Vend données?"
    },
    "prompts": {
      "completed": "Posez-moi n'importe quelle question sur vos clients ?",
      "question1": "Sont-ils autofinancés?",
      "question2": "Vendent-ils des données?"
    }
  }
}
</i18n>
